import type { Landmark } from '../../../data/types';
import type { Vec2, Ring } from '../../../geo/types';
import { stripPolygon } from '../../../geo/polyline';
import type { ReservedFootprint } from '../types';

/** Private footways are explicit, estimated source records; they do not create public streets. */
export function privateApproachFootprint(landmark: Landmark): Ring | null {
  const keys = [
    'privateApproachX0',
    'privateApproachY0',
    'privateApproachX1',
    'privateApproachY1',
    'privateApproachWidth',
  ];
  const values = keys.map((k) => landmark.params[k]);
  if (!values.every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  const [x0, y0, x1, y1, width] = values as number[];
  if (width! < 1 || width! > 4 || Math.hypot(x1! - x0!, y1! - y0!) < 0.01) return null;
  const points: Vec2[] = [
    [x0!, y0!],
    [x1!, y1!],
  ];
  return stripPolygon(points, [width!, width!]);
}

/** Both the architectural reservation and its access must stay clear of generated houses. */
export function collectReservedLandmarks(
  landmarks: readonly Landmark[]
): readonly ReservedFootprint[] {
  return landmarks.flatMap((l) => {
    const approach = privateApproachFootprint(l);
    return [
      { id: l.id, polygon: { outer: l.footprint, holes: [] } },
      ...(approach
        ? [{ id: `${l.id}:private-approach`, polygon: { outer: approach, holes: [] } }]
        : []),
    ];
  });
}
